import { useState } from "react";
import { Play, FileText, Trash2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AuditProgressIndicator } from "@/components/AuditProgressIndicator";
import { AuditResultsSummary } from "@/components/AuditResultsSummary";

interface SocialSellingSubmission {
  id: string;
  user_domain: string;
  email: string;
  phone: string;
  status: string;
  created_at: string;
  competitor_domains?: string[] | null;
  report_data?: any;
}

interface MobileSocialSellingCardProps {
  submission: SocialSellingSubmission;
  onStartAudit: () => void;
  onViewReport: () => void;
  onDelete: () => void;
  onRetryAudit?: () => void;
  formatPhoneNumber: (phone: string) => string;
}

export function MobileSocialSellingCard({
  submission,
  onStartAudit,
  onViewReport,
  onDelete,
  onRetryAudit,
  formatPhoneNumber,
}: MobileSocialSellingCardProps) {
  const [expanded, setExpanded] = useState(false);

  const hasLowConfidence = submission.report_data?.meta?.confidenceLevels && 
    Object.values(submission.report_data.meta.confidenceLevels).some((c: any) => c === 'low' || c === 'very-low');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500">Ukończony</Badge>;
      case "processing":
        return <Badge className="bg-blue-500">W trakcie</Badge>;
      case "failed":
        return <Badge className="bg-red-500">Błąd</Badge>;
      default:
        return <Badge variant="outline">Oczekujący</Badge>;
    }
  };

  return (
    <Card className="p-4 space-y-3">
      {/* Priority 1: Always visible */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base truncate">{submission.user_domain}</h3>
          <p className="text-sm text-muted-foreground truncate">{submission.email}</p>
        </div>
        {getStatusBadge(submission.status)}
      </div>

      <div className="text-sm">
        <span className="text-muted-foreground">Telefon: </span>
        <span className="font-medium">{formatPhoneNumber(submission.phone)}</span>
      </div>

      {/* Progress indicator for processing status */}
      {submission.status === 'processing' && !submission.report_data && (
        <div className="pt-2 border-t">
          <AuditProgressIndicator 
            status={submission.status}
            createdAt={submission.created_at}
          />
        </div>
      )}

      {/* Results summary for completed status */}
      {submission.status === 'completed' && submission.report_data && (
        <div className="pt-2 border-t">
          <AuditResultsSummary reportData={submission.report_data} expanded />
        </div>
      )}

      {/* Expandable section */}
      {expanded && (
        <div className="space-y-2 pt-2 border-t">
          {submission.competitor_domains && submission.competitor_domains.length > 0 && (
            <div>
              <span className="text-sm text-muted-foreground">Konkurencja:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {submission.competitor_domains.map((comp: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {comp}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Utworzono: {new Date(submission.created_at).toLocaleDateString("pl-PL")}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        {submission.competitor_domains && submission.competitor_domains.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="flex-1"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Zwiń
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Więcej
              </>
            )}
          </Button>
        )}

        {submission.status === "pending" && (
          <Button variant="outline" size="sm" onClick={onStartAudit} className="flex-1">
            <Play className="h-4 w-4 mr-2" />
            Start
          </Button>
        )}

        {submission.status === "completed" && (
          <>
            <Button variant="outline" size="sm" onClick={onViewReport} className="flex-1">
              <FileText className="h-4 w-4 mr-2" />
              Raport
            </Button>
            {hasLowConfidence && onRetryAudit && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRetryAudit} 
                className="flex-1"
                title="Ponów audyt z powodu niskiej pewności danych"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            )}
          </>
        )}

        <Button variant="outline" size="sm" onClick={onDelete} className="flex-1">
          <Trash2 className="h-4 w-4 mr-2" />
          Usuń
        </Button>
      </div>
    </Card>
  );
}
